import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import NumberInput from '../../components/NumberInput';
import { useNotification } from '../../contexts/NotificationContext';

const DURATION_OPTIONS = [15, 30, 45, 60, 75, 90, 105, 120];

function ServicesContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [categoryFilterBranchId, setCategoryFilterBranchId] = useState('');
  const [categoryModal, setCategoryModal] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ branch_id: '', name: '' });

  // Modal state
  const [selectedShopId, setSelectedShopId] = useState('');
  const [form, setForm] = useState({ branch_id: '', name: '', duration_minutes: 30, price: '', category_id: '' });

  // Branches filtered by the selected shop in the modal
  const filteredBranches = allBranches.filter(b => !selectedShopId || b.shop_id === parseInt(selectedShopId));

  const fetchData = async () => {
    try {
      const [svcData, shopData, branchData, catData] = await Promise.all([
        api.getAdminServices(),
        api.getAdminShops(),
        api.getBranches(),
        api.getAdminCategories(),
      ]);
      setServices(svcData);
      setShops(shopData);
      setAllBranches(branchData);
      setCategories(catData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const categoriesForBranch = (branchId) => categories.filter(c => c.branch_id === parseInt(branchId));
  const filteredCategories = !categoryFilterBranchId ? categories : categories.filter(c => c.branch_id === parseInt(categoryFilterBranchId));

  const saveCategory = async () => {
    if (!categoryForm.branch_id || !categoryForm.name?.trim()) { showAlert(t('admin.branchAndNameRequired')); return; }
    try {
      if (categoryModal?.id) await api.updateAdminCategory(categoryModal.id, { name: categoryForm.name.trim() });
      else await api.createAdminCategory({ branch_id: parseInt(categoryForm.branch_id), name: categoryForm.name.trim() });
      setCategoryModal(null);
      fetchData();
    } catch (err) { showAlert(err.message); }
  };

  const deleteCategory = async (id) => {
    showConfirm(t('admin.deleteCategoryConfirm'), async () => {
      try { await api.deleteAdminCategory(id); fetchData(); } catch (err) { showAlert(err.message); }
    });
  };

  const branchById = (id) => allBranches.find(b => b.id === id);
  const shopOfBranch = (branchId) => {
    const branch = branchById(branchId);
    if (!branch) return '—';
    return shops.find(s => s.id === branch.shop_id)?.name ?? '—';
  };

  const openCreate = () => {
    setEditing('create');
    setSelectedShopId('');
    setForm({ branch_id: '', name: '', duration_minutes: 30, price: '', category_id: '' });
  };

  const openEdit = (s) => {
    const branch = branchById(s.branch_id);
    const shopId = branch?.shop_id ?? '';
    setSelectedShopId(String(shopId));
    setEditing(s.id);
    setForm({ branch_id: s.branch_id, name: s.name, duration_minutes: s.duration_minutes, price: s.price, category_id: s.category_id ?? '' });
  };

  const openCategoryCreate = () => {
    setCategoryModal('create');
    setCategoryForm({ branch_id: categoryFilterBranchId || '', name: '' });
  };
  const openCategoryEdit = (c) => {
    setCategoryModal(c);
    setCategoryForm({ branch_id: c.branch_id, name: c.name });
  };

  const handleShopChange = (shopId) => {
    setSelectedShopId(shopId);
    setForm(f => ({ ...f, branch_id: '' })); // reset branch when shop changes
  };

  const save = async () => {
    if (!form.branch_id) { showAlert(t('admin.pleaseSelectBranch')); return; }
    try {
      const payload = {
        branch_id: parseInt(form.branch_id),
        name: form.name,
        duration_minutes: Number(form.duration_minutes) || 30,
        price: parseFloat(form.price),
        category_id: form.category_id ? parseInt(form.category_id) : null,
      };
      if (editing === 'create') await api.createAdminService(payload);
      else await api.updateAdminService(editing, { name: payload.name, duration_minutes: payload.duration_minutes, price: payload.price, category_id: payload.category_id });
      setEditing(null);
      fetchData();
    } catch (err) { showAlert(err.message); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteServiceConfirm'), async () => {
      try { await api.deleteAdminService(id); fetchData(); }
      catch (err) { showAlert(err.message); }
    });
  };

  return (
    <AdminLayout role="admin">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.services')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        <button style={S.btnOrange} onClick={openCreate}
          onMouseEnter={e => e.target.style.background = T.orangeHov}
          onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.addService')}</button>
      </div>

      {/* Categories */}
      <div style={{ marginBottom: '2.5rem' }}>
        <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.08em', color: T.white, marginBottom: '1rem' }}>{t('admin.categories')}</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <select value={categoryFilterBranchId} onChange={e => setCategoryFilterBranchId(e.target.value)} style={{ ...S.select, width: 'auto' }}>
            <option value="">{t('admin.allBranches')}</option>
            {allBranches.map(b => <option key={b.id} value={b.id}>{branchById(b.id)?.name ?? b.name} ({shopOfBranch(b.id)})</option>)}
          </select>
          <button style={S.btnOrange} onClick={openCategoryCreate}
            onMouseEnter={e => e.target.style.background = T.orangeHov}
            onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.addCategory')}</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead><tr>{[t('admin.id'), t('admin.branch'), t('admin.name'), t('admin.actions')].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {filteredCategories.map(c => (
                <tr key={c.id} onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>{c.id}</td>
                  <td style={S.td}>{branchById(c.branch_id)?.name ?? c.branch_id}</td>
                  <td style={S.td}>{c.name}</td>
                  <td style={S.td}>
                    <button style={S.btnSmall} onClick={() => openCategoryEdit(c)}>{t('common.edit')}</button>
                    <button style={S.btnDanger} onClick={() => deleteCategory(c.id)}>{t('common.delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {categoryModal && (
          <div style={S.modal}>
            <div style={S.modalBox}>
              <h2 style={S.modalTitle}>{categoryModal === 'create' ? t('admin.newCategory') : t('admin.editCategory')}</h2>
              <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginBottom: '0.4rem' }}>{t('admin.branch')}</label>
              <select value={categoryForm.branch_id} onChange={e => setCategoryForm({ ...categoryForm, branch_id: e.target.value })} style={S.select} disabled={categoryModal !== 'create'}>
                <option value="">{t('admin.chooseBranch')}</option>
                {allBranches.map(b => <option key={b.id} value={b.id}>{b.name} ({shopOfBranch(b.id)})</option>)}
              </select>
              <input placeholder={t('admin.categoryName')} value={categoryForm.name} onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })} style={S.input} />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={saveCategory} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
                <button onClick={() => setCategoryModal(null)} style={S.btnOutline}>{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newService') : t('admin.editService')}</h2>

            {/* Step 1: Select Salon */}
            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
              1. {t('admin.selectSalon')}
            </label>
            <select value={selectedShopId} onChange={e => handleShopChange(e.target.value)} style={S.select}>
              <option value="">{t('admin.chooseSalon')}</option>
              {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            {/* Step 2: Select Branch (filtered by salon) */}
            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
              2. {t('admin.selectBranch')}
            </label>
            <select
              value={form.branch_id}
              onChange={e => setForm({ ...form, branch_id: e.target.value })}
              style={S.select}
              disabled={!selectedShopId}
            >
              <option value="">{selectedShopId ? t('admin.chooseBranch') : t('admin.selectSalonFirst')}</option>
              {filteredBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>

            <input placeholder={t('admin.serviceName')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
              {t('admin.durationSteps')}
            </label>
            <select value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: Number(e.target.value) })} style={S.select}>
              {DURATION_OPTIONS.map(m => <option key={m} value={m}>{m} {t('admin.min')}</option>)}
            </select>

            <NumberInput placeholder={t('admin.priceEgp')} value={form.price} onChange={(v) => setForm({ ...form, price: v })} inputStyle={S.input} min={0} step={0.01} />

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
              {t('admin.categoryOptional')}
            </label>
            <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} style={S.select} disabled={!form.branch_id}>
              <option value="">{t('admin.none')}</option>
              {form.branch_id && categoriesForBranch(form.branch_id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{[t('admin.id'), t('admin.name'), t('admin.category'), t('admin.salon'), t('admin.branch'), t('admin.duration'), t('admin.price'), t('admin.actions')].map((h, i) => (
                <th key={i} style={S.th}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {services.map(s => (
                <tr key={s.id}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>{s.id}</td>
                  <td style={{ ...S.td, fontWeight: 500, color: T.white }}>{s.name}</td>
                  <td style={S.td}>{s.category_name ?? '—'}</td>
                  <td style={{ ...S.td, color: T.orange, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', letterSpacing: '0.05em' }}>
                    {shopOfBranch(s.branch_id)}
                  </td>
                  <td style={S.td}>{branchById(s.branch_id)?.name ?? s.branch_id}</td>
                  <td style={S.td}>{s.duration_minutes} min</td>
                  <td style={{ ...S.td, color: T.orange }}>{s.price} EGP</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button style={S.btnSmall} onClick={() => openEdit(s)}
                        onMouseEnter={e => e.target.style.color = T.white}
                        onMouseLeave={e => e.target.style.color = T.muted}>{t('common.edit')}</button>
                      <button style={S.btnDanger} onClick={() => remove(s.id)}>{t('common.delete')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Services() {
  return <ProtectedRoute requireSuperAdmin><ServicesContent /></ProtectedRoute>;
}
